import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, Container, Grid, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

const normalizeList = (payload, fallback = []) => {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.items)) {
        return payload.items;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    return fallback;
};

const fetchRemoteItems = async (url, fallback = []) => {
    if (!url) {
        return fallback;
    }

    try {
        const response = await fetch(url);
        if (!response.ok) {
            return fallback;
        }
        const payload = await response.json();
        return normalizeList(payload, fallback);
    } catch (error) {
        return fallback;
    }
};

const InsightsParallelPanels = ({ sectionConfig = {}, layoutContext = {} }) => {
    const { getIcon, getAnimationProps } = layoutContext;
    const [openFaqIndex, setOpenFaqIndex] = useState(0);
    const [whyUseItems, setWhyUseItems] = useState(sectionConfig.whyUse?.items || []);
    const [faqItems, setFaqItems] = useState(sectionConfig.faq?.items || []);

    useEffect(() => {
        let isSubscribed = true;

        const loadRemoteData = async () => {
            const [loadedWhyUse, loadedFaq] = await Promise.all([
                fetchRemoteItems(sectionConfig.whyUse?.dataUrl, sectionConfig.whyUse?.items || []),
                fetchRemoteItems(sectionConfig.faq?.dataUrl, sectionConfig.faq?.items || [])
            ]);

            if (isSubscribed) {
                setWhyUseItems(loadedWhyUse);
                setFaqItems(loadedFaq);
            }
        };

        loadRemoteData();
        return () => {
            isSubscribed = false;
        };
    }, [sectionConfig.whyUse?.dataUrl, sectionConfig.faq?.dataUrl]);

    const applyAnimation = (animationConfig, index = 0) => (
        typeof getAnimationProps === 'function' ? getAnimationProps(animationConfig, index) : {}
    );

    const sectionAnimation = applyAnimation(sectionConfig.animation?.section);
    const leftPanelAnimation = applyAnimation(sectionConfig.animation?.leftPanel);
    const rightPanelAnimation = applyAnimation(sectionConfig.animation?.rightPanel);

    const resolvedWhyUse = useMemo(() => normalizeList(whyUseItems, sectionConfig.whyUse?.items || []), [whyUseItems, sectionConfig.whyUse?.items]);
    const resolvedFaqs = useMemo(() => normalizeList(faqItems, sectionConfig.faq?.items || []), [faqItems, sectionConfig.faq?.items]);

    if (!resolvedWhyUse.length && !resolvedFaqs.length) {
        return null;
    }

    return (
        <Box
            id="insights-section"
            className={sectionAnimation.className}
            data-anim={sectionAnimation.dataAnim}
            data-delay={sectionAnimation.dataDelay}
            data-trigger-once={sectionAnimation.dataTriggerOnce}
            style={{ ...(sectionConfig.styles || {}), ...(sectionAnimation.style || {}) }}
        >
            <Container maxWidth={sectionConfig.container?.maxWidth || 'lg'} style={sectionConfig.containerStyles || {}}>
                <Grid container spacing={sectionConfig.grid?.spacing || 3} alignItems="stretch">
                    <Grid
                        item
                        xs={sectionConfig.leftPanel?.gridItem?.xs || 12}
                        md={sectionConfig.leftPanel?.gridItem?.md || 6}
                        style={{ display: 'flex' }}
                    >
                        <Card
                            className={leftPanelAnimation.className}
                            data-anim={leftPanelAnimation.dataAnim}
                            data-delay={leftPanelAnimation.dataDelay}
                            data-trigger-once={leftPanelAnimation.dataTriggerOnce}
                            style={{ height: '100%', width: '100%', ...(sectionConfig.leftPanel?.styles || {}), ...(leftPanelAnimation.style || {}) }}
                        >
                            <Typography style={sectionConfig.leftPanel?.titleStyles || {}}>
                                {sectionConfig.leftPanel?.title || sectionConfig.whyUse?.title || 'Why Use'}
                            </Typography>
                            <Box style={sectionConfig.leftPanel?.listStyles || {}}>
                                {resolvedWhyUse.map((item, index) => (
                                    <Box key={`why-use-item-${index}`} style={sectionConfig.leftPanel?.listItemStyles || {}}>
                                        <Box style={sectionConfig.leftPanel?.bulletStyles || {}}>
                                            {item.icon
                                                ? getIcon?.(item.icon, { style: { ...(sectionConfig.leftPanel?.bulletIconStyles || {}), ...(item.iconStyles || {}) } })
                                                : <Box style={sectionConfig.leftPanel?.dotStyles || {}} />}
                                        </Box>
                                        <Typography style={sectionConfig.leftPanel?.itemTextStyles || {}}>
                                            {item.text || item.title || item}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Card>
                    </Grid>

                    <Grid
                        item
                        xs={sectionConfig.rightPanel?.gridItem?.xs || 12}
                        md={sectionConfig.rightPanel?.gridItem?.md || 6}
                        style={{ display: 'flex' }}
                    >
                        <Card
                            className={rightPanelAnimation.className}
                            data-anim={rightPanelAnimation.dataAnim}
                            data-delay={rightPanelAnimation.dataDelay}
                            data-trigger-once={rightPanelAnimation.dataTriggerOnce}
                            style={{ height: '100%', width: '100%', ...(sectionConfig.rightPanel?.styles || {}), ...(rightPanelAnimation.style || {}) }}
                        >
                            <Typography style={sectionConfig.rightPanel?.titleStyles || {}}>
                                {sectionConfig.rightPanel?.title || sectionConfig.faq?.title || 'Frequently Asked Questions'}
                            </Typography>
                            <Box style={sectionConfig.rightPanel?.accordionStyles || {}}>
                                {resolvedFaqs.map((faq, index) => {
                                    const isOpen = openFaqIndex === index;
                                    return (
                                        <Box
                                            key={`faq-item-${index}`}
                                            style={{
                                                ...(sectionConfig.rightPanel?.accordionItemStyles || {}),
                                                ...(isOpen ? (sectionConfig.rightPanel?.accordionItemOpenStyles || {}) : {})
                                            }}
                                            onClick={() => setOpenFaqIndex((previous) => (previous === index ? -1 : index))}
                                        >
                                            <Box style={sectionConfig.rightPanel?.questionRowStyles || {}}>
                                                <Typography style={sectionConfig.rightPanel?.questionStyles || {}}>
                                                    {faq.question || faq.title || faq}
                                                </Typography>
                                                <ExpandMoreIcon
                                                    style={{
                                                        ...(sectionConfig.rightPanel?.expandIconStyles || {}),
                                                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                        transition: 'transform 0.2s ease'
                                                    }}
                                                />
                                            </Box>
                                            {isOpen && (
                                                <Typography style={sectionConfig.rightPanel?.answerStyles || {}}>
                                                    {faq.answer || faq.description || ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default InsightsParallelPanels;
